/**
 * Catalogo attributi della società (V52).
 *
 * L'Admin crea, rinomina e disattiva. Ogni attributo si valuta da 1 a 100
 * e vale per tutti i ruoli; la famiglia si sceglie solo alla creazione. L'elenco va per
 * famiglia e poi per nome. Un attributo con misurazioni non si cancella.
 */

import { type FormEvent, useState } from 'react';
import { OctagonXIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import {
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  FAMIGLIE_ATTRIBUTO,
  creaAttributoSchema,
  modificaAttributoSchema,
  type FamigliaAttributo,
} from '@calcio/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldContent, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MenuAzioni, PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import type { AttributoCatalogo } from './matrice.js';
import { useConferma } from '../comuni/Conferma.js';

type InModifica = Partial<AttributoCatalogo> | null;

export function CatalogoAttributi() {
  const {
    dati: attributi,
    errore,
    ricarica,
  } = useDati<AttributoCatalogo[]>('/amministrazione/attributi');
  const [inModifica, setInModifica] = useState<InModifica>(null);
  const [chiedi, conferma] = useConferma();

  if (!attributi)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  const cancella = async (a: AttributoCatalogo) => {
    const si = await chiedi({
      titolo: `Cancellare ${a.nome}?`,
      testo: 'Esce dal catalogo. Si cancella solo un attributo senza misurazioni.',
      azione: 'Cancella',
      distruttiva: true,
    });
    if (!si) return;
    try {
      await chiama(`/amministrazione/attributi/${a.id}`, { metodo: 'DELETE' });
      await ricarica();
      toast.success(`${a.nome} cancellato.`);
    } catch (e) {
      toast.error(messaggio(e));
    }
  };

  return (
    <>
      <Testata
        titolo="Catalogo attributi"
        voce="catalogo-attributi"
        azione={<PulsanteNuovo etichetta="Nuovo attributo" onClick={() => setInModifica({})} />}
      />
      <Corpo>
        <div className="flex flex-col gap-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Famiglia</TableHead>
                <TableHead>
                  <span className="sr-only">Azioni</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attributi.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      {a.nome}
                      {!a.attivo && <Badge variant="secondary">Disattivato</Badge>}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{ETICHETTA_FAMIGLIA_ATTRIBUTO[a.famiglia]}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <MenuAzioni
                        riga
                        etichetta={`Azioni su ${a.nome}`}
                        voci={[
                          {
                            etichetta: 'Modifica',
                            Icona: PencilIcon,
                            onSelect: () => setInModifica(a),
                          },
                          {
                            etichetta: 'Elimina',
                            Icona: Trash2Icon,
                            onSelect: () => void cancella(a),
                            distruttiva: true,
                          },
                        ]}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Dialog
          open={inModifica !== null}
          onOpenChange={(aperto) => !aperto && setInModifica(null)}
        >
          <DialogContent>
            {inModifica && (
              <FormAttributo
                key={inModifica.id ?? 'nuovo'}
                attributo={inModifica}
                onChiudi={() => setInModifica(null)}
                onSalvato={async () => {
                  setInModifica(null);
                  await ricarica();
                }}
              />
            )}
          </DialogContent>
        </Dialog>
        {conferma}
      </Corpo>
    </>
  );
}

function FormAttributo({
  attributo,
  onChiudi,
  onSalvato,
}: {
  attributo: Partial<AttributoCatalogo>;
  onChiudi: () => void;
  onSalvato: () => Promise<void>;
}) {
  const [nome, setNome] = useState(attributo.nome ?? '');
  const [famiglia, setFamiglia] = useState<FamigliaAttributo | ''>(attributo.famiglia ?? '');
  const [attivo, setAttivo] = useState(attributo.attivo ?? true);
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = attributo.id
      ? modificaAttributoSchema.safeParse({ nome, attivo })
      : creaAttributoSchema.safeParse({ nome, famiglia });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(
        attributo.id ? `/amministrazione/attributi/${attributo.id}` : '/amministrazione/attributi',
        { metodo: attributo.id ? 'PUT' : 'POST', corpo: esito.data },
      );
      await onSalvato();
      toast.success(`${esito.data.nome ?? nome} salvato.`);
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>{attributo.id ? `Modifica ${attributo.nome}` : 'Nuovo attributo'}</DialogTitle>
        <DialogDescription>
          {attributo.id
            ? 'La famiglia resta quella della creazione.'
            : 'La famiglia si sceglie alla creazione. Si valuta da 1 a 100, per tutti i ruoli.'}
        </DialogDescription>
      </DialogHeader>

      <FieldGroup>
        <Field data-invalid={Boolean(errori.nome)}>
          <FieldLabel htmlFor="attributo-nome">Nome</FieldLabel>
          <Input
            id="attributo-nome"
            value={nome}
            aria-invalid={Boolean(errori.nome)}
            onChange={(e) => setNome(e.target.value)}
          />
          <FieldError>{errori.nome}</FieldError>
        </Field>

        {attributo.id ? (
          <Field orientation="horizontal">
            <FieldContent>
              <FieldLabel htmlFor="attributo-attivo">Attivo</FieldLabel>
            </FieldContent>
            <Switch id="attributo-attivo" checked={attivo} onCheckedChange={setAttivo} />
          </Field>
        ) : (
          <Field data-invalid={Boolean(errori.famiglia)}>
            <FieldLabel htmlFor="attributo-famiglia">Famiglia</FieldLabel>
            <Select
              value={famiglia || undefined}
              onValueChange={(v) => setFamiglia(v as FamigliaAttributo)}
            >
              <SelectTrigger
                id="attributo-famiglia"
                className="w-full"
                aria-invalid={Boolean(errori.famiglia)}
              >
                <SelectValue placeholder="Scegli la famiglia" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {FAMIGLIE_ATTRIBUTO.map((f) => (
                    <SelectItem key={f} value={f}>
                      {ETICHETTA_FAMIGLIA_ATTRIBUTO[f]}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <FieldError>{errori.famiglia}</FieldError>
          </Field>
        )}
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          {attributo.id ? 'Salva' : 'Crea attributo'}
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
